/**
 * MODULE: core/auth/frontend/components/ProtectedRoute.tsx
 * PURPOSE: Guard routes requiring authentication; redirect to fallbackPath when not authenticated.
 *
 * DEPENDENCIES: react, react-router-dom (Navigate, useLocation), ../hooks/useAuth, ../../contracts/auth.types
 * PUBLIC API: ProtectedRoute({ children, requiredRoles?, fallbackPath? })
 * SIDE EFFECTS: Navigation redirect to fallbackPath or /unauthorized
 * AGENT_SAFETY: 🔴 PROTECTED - Auth flow; do not remove or bypass.
 * Recent Changes: 2026-02-08 – fallbackPath default changed from '/login' to '/auth' for shell consistency.
 */
import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import type { UserRole } from '../../contracts/auth.types';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: UserRole[];
  fallbackPath?: string;
}

export function ProtectedRoute({
  children,
  requiredRoles,
  fallbackPath = '/auth',
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="protected-route-loading">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  if (requiredRoles && user && !requiredRoles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}
